import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";

const API_URL = process.env.REACT_APP_API_URL;

const DEFAULT_SORT_BY = "giDate";
const DEFAULT_SORT_ORDER = "desc";
const FILTER_KEY = "giSupplierListFilters";

const SORT_COLUMN_MAP = {
  giNumber: "giNumber",
  giDate: "giDate",
  poNumber: "poNumber",
};

const SupplierGiStatCardSkeleton = () => (
  <div className="procurement-stat-col">
    <div className="card supplier-stat-card">
      <div className="card-body">
        <div className="stat-skeleton stat-skeleton-title" aria-hidden="true" />
        <div className="stat-skeleton stat-skeleton-value" aria-hidden="true" />
      </div>
    </div>
  </div>
);

const SupplierGiStatCard = ({ label, value, titleColor, valueColor }) => (
  <div className="procurement-stat-col">
    <div className="card supplier-stat-card procurement-stat-card-active">
      <div className="card-body">
        <div className="stat-title" style={{ color: titleColor }}>
          {label}
        </div>
        <div className="stat-value" style={{ color: valueColor }}>
          {value}
        </div>
      </div>
    </div>
  </div>
);


function parseJsonIfString(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function parseApiResponse(raw) {
  let d = parseJsonIfString(raw);
  if (d?.body != null) return parseJsonIfString(d.body);
  return d;
}

function isApiSuccess(res) {
  if (!res || typeof res !== "object") return false;
  const s = res.success;
  return s === true || s === "true" || s === 1 || s === "1";
}

function extractGiPayload(res) {
  const block = parseJsonIfString(res?.data != null ? res.data : res);
  if (!block || typeof block !== "object") return { list: [], total: 0 };
  const raw = parseJsonIfString(block.goodsInwards || block.items || []);
  const list = Array.isArray(raw) ? raw : [];
  const total =
    typeof block.totalCount === "number" ? block.totalCount : list.length;
  return { list, total };
}

function formatGiDate(value) {
  if (!value) return "-";
  const str = String(value).trim();
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) return str;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return str || "-";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  });
}

function getGiId(gi) {
  return gi?._id || gi?.id || "";
}

function renderMaterialCodes(gi) {
  const list = Array.isArray(gi?.materials) ? gi.materials : [];
  const codes = [
    ...new Set(
      list.map((m) => String(m?.materialCode || "").trim()).filter(Boolean),
    ),
  ];
  if (codes.length === 0) return "-";
  return (
    <span style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
      {codes.join(", ")}
    </span>
  );
}


const GiSupplierList = () => {
  const savedFilters = JSON.parse(sessionStorage.getItem(FILTER_KEY) || "{}");

  const [gis, setGis] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCountLoading, setIsCountLoading] = useState(true);
  const [giCount, setGiCount] = useState({ totalCount: 0 });
  const [searchQuery, setSearchQuery] = useState(savedFilters.searchQuery || "");
  const [activeSearchQuery, setActiveSearchQuery] = useState(
    savedFilters.activeSearchQuery || "",
  );
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(savedFilters.currentPage || 1);
  const [itemsPerPage, setItemsPerPage] = useState(
    savedFilters.itemsPerPage || 25,
  );
  const [sortBy, setSortBy] = useState(savedFilters.sortBy || DEFAULT_SORT_BY);
  const [sortOrder, setSortOrder] = useState(
    savedFilters.sortOrder || DEFAULT_SORT_ORDER,
  );
  const loadRequestIdRef = useRef(0);

  const hasActiveFilters = Boolean(activeSearchQuery);

  useEffect(() => {
    sessionStorage.setItem(
      FILTER_KEY,
      JSON.stringify({
        searchQuery,
        activeSearchQuery,
        currentPage,
        itemsPerPage,
        sortBy,
        sortOrder,
      }),
    );
  }, [searchQuery, activeSearchQuery, currentPage, itemsPerPage, sortBy, sortOrder]);

  const fetchGiCount = useCallback(
    async (requestId) => {
      setIsCountLoading(true);
      try {
        const payload = {
          action: "get-goods-inwards-supplier-count",
          currentUserId: localStorage.getItem("id") || undefined,
        };
        const q = activeSearchQuery.trim();
        if (q) payload.search = q;

        const response = await axios.post(API_URL, payload);
        if (requestId !== loadRequestIdRef.current) return;

        const res = parseApiResponse(response.data);
        if (isApiSuccess(res)) {
          const data = parseJsonIfString(res.data);
          const d = data && typeof data === "object" ? data : {};
          setGiCount({ totalCount: Number(d.totalCount) || 0 });
        }
      } catch (error) {
        if (requestId !== loadRequestIdRef.current) return;
        console.error("Supplier GI Stats Error:", error);
      } finally {
        if (requestId === loadRequestIdRef.current) setIsCountLoading(false);
      }
    },
    [activeSearchQuery],
  );

  const fetchGis = useCallback(
    async (requestId) => {
      setIsLoading(true);
      try {
        const payload = {
          action: "get-goods-inwards-supplier",
          currentUserId: localStorage.getItem("id") || undefined,
          page: currentPage,
          limit: itemsPerPage,
          sortBy,
          sortOrder,
        };
        const q = activeSearchQuery.trim();
        if (q) payload.search = q;

        const response = await axios.post(API_URL, payload);
        if (requestId !== loadRequestIdRef.current) return;

        const res = parseApiResponse(response.data);
        if (isApiSuccess(res)) {
          const { list, total } = extractGiPayload(res);
          setGis(list);
          setTotalCount(total);
        } else {
          toast.error(res?.message || "Failed to fetch supplier GI", {
            autoClose: 2000,
          });
          setGis([]);
          setTotalCount(0);
        }
      } catch (error) {
        if (requestId !== loadRequestIdRef.current) return;
        console.error("Fetch supplier GI error:", error);
        const errData = error.response?.data
          ? parseApiResponse(error.response.data)
          : null;
        toast.error(
          errData?.message || "An error occurred while fetching supplier GI",
          { autoClose: 2000 },
        );
        setGis([]);
        setTotalCount(0);
      } finally {
        if (requestId === loadRequestIdRef.current) setIsLoading(false);
      }
    },
    [currentPage, itemsPerPage, activeSearchQuery, sortBy, sortOrder],
  );

  useEffect(() => {
    const requestId = ++loadRequestIdRef.current;
    let cancelled = false;
    const load = async () => {
      await fetchGiCount(requestId);
      if (cancelled || requestId !== loadRequestIdRef.current) return;
      await fetchGis(requestId);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [fetchGis, fetchGiCount]);

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage) || 1);
  const startItem = gis.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem =
    gis.length > 0 ? Math.min(currentPage * itemsPerPage, totalCount) : 0;

   const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (value.trim() === "" && activeSearchQuery) {
      setActiveSearchQuery("");
      setCurrentPage(1);
    }
  };

  const handleSearchTrigger = () => {
    setActiveSearchQuery(searchQuery.trim());
    setCurrentPage(1);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchTrigger();
    }
  };

  const handleClearAll = () => {
    setSearchQuery("");
    setActiveSearchQuery("");
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value) || 25);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
    }
  };

  const handleSort = (column) => {
    const apiField = SORT_COLUMN_MAP[column] || column;
    if (sortBy === apiField) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(apiField);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  const getSortIcon = (column) => {
    const apiField = SORT_COLUMN_MAP[column] || column;
    if (sortBy !== apiField) {
      return (
        <i
          className="fa-solid fa-sort ml-1 text-muted"
          style={{ fontSize: "0.8em", flexShrink: 0 }}
        />
      );
    }
    return (
      <i
        className={`fa-solid ${
          sortOrder === "asc" ? "fa-sort-up" : "fa-sort-down"
        } ml-1`}
        style={{ fontSize: "0.8em", flexShrink: 0 }}
      />
    );
  };

  const renderSortableHeader = (column, label, extraStyle = {}) => (
    <th
      onClick={() => handleSort(column)}
      style={{ cursor: "pointer", whiteSpace: "nowrap", ...extraStyle }}
    >
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        {label}
        {getSortIcon(column)}
      </span>
    </th>
  );

  const getPageNumbers = () => {
    const pages = [];
    const max = 7;
    const tp = totalPages;
    if (tp <= max) {
      for (let i = 1; i <= tp; i++) pages.push(i);
    } else if (currentPage <= 4) {
      for (let i = 1; i <= 5; i++) pages.push(i);
      pages.push("ellipsis", tp);
    } else if (currentPage >= tp - 3) {
      pages.push(1, "ellipsis");
      for (let i = tp - 4; i <= tp; i++) pages.push(i);
    } else {
      pages.push(1, "ellipsis");
      for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
      pages.push("ellipsis", tp);
    }
    return pages;
  };

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-sm-6">
              <h1 className="m-0 txt_lft">Goods Inwards</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="procurement-stat-card-row rfq-stat-card-row">
                {isCountLoading ? (
                  <SupplierGiStatCardSkeleton />
                ) : (
                  <SupplierGiStatCard
                    label="Total GI"
                    value={giCount.totalCount}
                    titleColor="#196F3D"
                    valueColor="#27AE60"
                  />
                )}
              </div>

              <div>
                <div className="card-body">
                  <div className="row align-items-end mb-4 flex-xl-nowrap">
                    <div className="col-12 col-sm-6 col-md-4 col-xl-3 mb-2 mb-xl-0">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          Search
                        </label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search GI..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onKeyPress={handleSearchKeyPress}
                          />
                          <div className="input-group-append">
                            <div
                              className="input-group-text crsr_pntr"
                              onClick={handleSearchTrigger}
                              style={{ cursor: "pointer" }}
                            >
                              <i className="fa-solid fa-search" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {isLoading && gis.length === 0 ? (
                <div className="text-center p-4">
                  <PulseLoader color="#1199B2" size={10} />
                </div>
              ) : gis.length === 0 && !hasActiveFilters ? (
                <div className="nocontent_div text-center p-4">
                  There are no goods inwards yet.
                </div>
              ) : gis.length === 0 && hasActiveFilters ? (
                <div className="nocontent_div text-center p-4">
                  No goods inwards found matching your search.
                  <br />
                  <div className="mt-3">
                    <button
                      type="button"
                      className="btn btn-blue"
                      onClick={handleClearAll}
                    >
                      Clear Search
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className="supply-table-container"
                  style={{ position: "relative" }}
                >
                  {isLoading && (
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(255, 255, 255, 0.55)",
                        pointerEvents: "none",
                      }}
                    >
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  )}
                  <div
                    className="table-responsive"
                    style={{
                      opacity: isLoading ? 0.6 : 1,
                      transition: "opacity 0.15s ease",
                      pointerEvents: isLoading ? "none" : "auto",
                    }}
                  >
                    <table
                      id="supplierGis"
                      width="100%"
                      className="table table-striped table-bordered txt_lft"
                    >
                      <thead>
                        <tr>
                          <th
                            style={{
                              whiteSpace: "nowrap",
                              width: "40px",
                              minWidth: "40px",
                            }}
                          >
                            S.No
                          </th>
                          {renderSortableHeader("giNumber", "GI Number", {
                            minWidth: "120px",
                          })}
                          {renderSortableHeader("giDate", "GI Date", {
                            minWidth: "100px",
                          })}
                          <th style={{ minWidth: "160px" }}>Material Code</th>
                          {renderSortableHeader("poNumber", "PO Number", {
                            minWidth: "120px",
                          })}
                          <th style={{ width: "80px", minWidth: "80px" }}>
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {gis.map((gi, index) => {
                          const gid = getGiId(gi);
                          return (
                            <tr key={gid || index}>
                              <td>
                                {(currentPage - 1) * itemsPerPage + index + 1}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {gi?.giNumber || "-"}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {formatGiDate(gi?.giDate)}
                              </td>
                              <td
                                style={{
                                  maxWidth: "240px",
                                  wordWrap: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                {renderMaterialCodes(gi)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {gi?.poNumber || "-"}
                              </td>
                              <td>
                                <div
                                  align="center"
                                  style={{ whiteSpace: "nowrap" }}
                                  className="d-flex justify-content-center align-items-center"
                                >
                                  <Link
                                    to={`${"/giSupplierView"}/${gid}`}
                                    className="crsr_pntr"
                                    title="View"
                                  >
                                    <i className="fa-solid fa-eye" />
                                  </Link>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div
                    className="supply-pagination-container"
                    style={{
                      opacity: isLoading ? 0.65 : 1,
                      pointerEvents: isLoading ? "none" : "auto",
                    }}
                  >
                    <div className="pagination-wrapper">
                      <div className="pagination-info">
                        <label
                          className="mb-0 mr-2"
                          style={{ fontSize: "13px" }}
                        >
                          Show:
                        </label>
                        <select
                          className="form-control form-control-sm"
                          value={itemsPerPage}
                          onChange={handleItemsPerPageChange}
                        >
                          <option value="25">25</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                        </select>
                        <span className="ml-2" style={{ fontSize: "13px" }}>
                          entries
                        </span>
                        <span
                          className="ml-3 text-muted"
                          style={{ fontSize: "13px" }}
                        >
                          {startItem}–{endItem} of {totalCount}
                        </span>
                      </div>

                      <nav className="pagination-nav">
                        <ul className="pagination mb-0">
                          <li
                            className={`page-item ${
                              currentPage === 1 ? "disabled" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(1)}
                              disabled={currentPage === 1}
                              title="First Page"
                            >
                              <i className="fa-solid fa-angle-double-left" />
                            </button>
                          </li>
                          <li
                            className={`page-item ${
                              currentPage === 1 ? "disabled" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1}
                              title="Previous Page"
                            >
                              <i className="fa-solid fa-angle-left" />
                            </button>
                          </li>
                          {getPageNumbers().map((page, index) =>
                            page === "ellipsis" ? (
                              <li
                                key={`ellipsis-${index}`}
                                className="page-item disabled"
                              >
                                <span className="page-link">...</span>
                              </li>
                            ) : (
                              <li
                                key={page}
                                className={`page-item ${
                                  currentPage === page ? "active" : ""
                                }`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() => handlePageChange(page)}
                                >
                                  {page}
                                </button>
                              </li>
                            ),
                          )}
                          <li
                            className={`page-item ${
                              currentPage === totalPages ? "disabled" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage === totalPages}
                              title="Next Page"
                            >
                              <i className="fa-solid fa-angle-right" />
                            </button>
                          </li>
                          <li
                            className={`page-item ${
                              currentPage === totalPages ? "disabled" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(totalPages)}
                              disabled={currentPage === totalPages}
                              title="Last Page"
                            >
                              <i className="fa-solid fa-angle-double-right" />
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GiSupplierList;